import { useCallback, useEffect, useState } from "react";

import { useApolloClient } from "@apollo/client";
import CloseIcon from "@hl/base-components/lib/assets/icons.generated/CloseX";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import {
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Flex,
  Loader,
  Tabs,
  Text,
  UnstyledButton,
} from "@mantine/core";

import useMintState from "../../../hooks/useMintState";
import { EmbedCardProps } from "../EmbedCard";
import { MintCardInnerProps } from "../MintVector/MintCard";
import MintCardButton, {
  MintButtonPosition,
} from "../MintVector/MintCardButton";
import { setCacheTokenMinted } from "../cache";
import MintedBadge from "../header/MintedBadge";
import { useSuccessModalData } from "../hooks";
import { useSeriesTokensQuery } from "../queries.graphql.generated";

type Props = MintCardInnerProps &
  Omit<EmbedCardProps, "flags"> & {
    onClose: () => void;
    mintingTokenId?: string;
  };

const useStyles = createStyles({
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "100%",
    backgroundColor: "white",
  },
  scrollable: {
    overflowY: "auto",
    overflowX: "hidden",
    height: "calc(100% - 86px)",
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    height: 56,
    alignItems: "center",
    paddingLeft: 16,
    paddingRight: 16,
  },
  token: {
    position: "relative",
  },
});

const IMAGE_FIT_SIZE = 400;

enum TabState {
  All = "all",
  Available = "available",
}

export const SeriesTokensPanel = ({
  onClose,
  mintingTokenId,
  collectionId,
  ...props
}: Props) => {
  const { classes } = useStyles();
  const { loadingClaimMint, isActiveMint, handleMint, collection } =
    useMintState();
  const { cache } = useApolloClient();
  const [selectedTab, setSelectedTab] = useState<string | null>(TabState.All);

  const {
    data: tokensData,
    loading,
    fetchMore,
  } = useSeriesTokensQuery({
    variables: {
      collectionId,
      limit: 3,
      cursor: null,
      minted: selectedTab === TabState.Available ? false : null,
    },
  });

  const seriesTokens = tokensData?.getPublicCollection?.seriesTokens;
  const tokens = seriesTokens?.edges ?? [];
  const nextCursor = seriesTokens?.pageInfo?.endCursor;

  const successData = useSuccessModalData();
  useEffect(() => {
    if (successData && successData.collectionId === collectionId) {
      const { tokensIds } = successData;
      if (!tokensIds) {
        return;
      }

      // update token minted status in cache
      tokensIds.forEach((id) => setCacheTokenMinted(cache, id));
    }
  }, [successData, collectionId, cache]);

  const loadMore = useCallback(
    () =>
      fetchMore({
        variables: {
          cursor: nextCursor,
        },
      }),
    [nextCursor]
  );

  const handleTabChange = useCallback(
    (tab: string | null) => {
      setSelectedTab(tab);
    },
    [setSelectedTab]
  );

  return (
    <div className={classes.root}>
      <div className={classes.detailsHeader}>
        <Tabs
          value={selectedTab}
          styles={{ tabsList: { height: 56 } }}
          onTabChange={handleTabChange}
        >
          <Tabs.List>
            <Tabs.Tab value={TabState.All}>All tokens</Tabs.Tab>
            <Tabs.Tab value={TabState.Available}>Available tokens</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <UnstyledButton
          onClick={onClose}
          aria-label="close details"
          p={5}
          pt={10}
        >
          <CloseIcon width={8} fill="#000" />
        </UnstyledButton>
      </div>
      <Divider />
      <div className={classes.scrollable}>
        {!tokens.length && (
          <Center>
            <Loader mt="lg" />
          </Center>
        )}
        {tokens.map((token, index) => (
          <Box key={index} mb={40} className={classes.token}>
            <ResizedImage
              src={token.imageUrl}
              fit="contain"
              fitSize={IMAGE_FIT_SIZE}
              styles={{ root: { minHeight: 200 } }}
            />
            {token.minted && <MintedBadge />}
            <Flex justify={"space-between"} align="flex-start" mt={12}>
              <Text>{token.name}</Text>
              <MintCardButton
                {...props}
                showWarningModals
                tokenId={token.id}
                onMintClick={handleMint}
                loading={mintingTokenId === token.id && loadingClaimMint}
                showErrorIcon={false}
                size="sm"
                px={14}
                h={32}
                enableCrossChainMint={
                  collection?.flagVariations.enableCrossChainMinting
                }
                disabledMint={
                  token.minted || (isActiveMint && mintingTokenId !== token.id)
                }
                buttonPosition={MintButtonPosition.SeriesPanel}
              />
            </Flex>
          </Box>
        ))}
        {nextCursor && (
          <Center>
            <Button
              color="gray.0"
              onClick={loadMore}
              fullWidth
              loading={loading}
            >
              Load more
            </Button>
          </Center>
        )}
      </div>
    </div>
  );
};
